import { reportWarning } from '../reportError';
import { stripHiddenContext, extractHiddenContext, getToolUseIdForResult } from './chatHelpers';
import { TextChirpContentBlockTypename, ToolResultChirpContentBlockTypename, ContextChirpContentBlockTypename, DisplayOnlyChirpContentBlockTypename } from '../../../__generated__/chirp/com/hubspot/breeze/engine/rpc/BreezeEngineStreamingService';
import { convertAppCitationEntry, parseCitationsFromToolResult } from './citationUtils';
import { normalizeBlock } from './normalizeBlock';
import { buildCompanionMap, buildToolPairs, promoteCompanionEntries, isToolUseBlock, isToolResultContent, isDisplayOnlyBlock } from './toolPairing';
import { buildArtifactBlocksFromDetected, buildArtifactBlocksFromWriteEntries, extractArtifactToolCalls, extractArtifactWriteEntries } from './artifactDetection';
import { resolveCitations } from './resolveCitations';
function parseWrapperBlocks(content) {
  try {
    const parsed = JSON.parse(content);
    if (parsed === null || typeof parsed !== 'object') return null;
    const blocks = parsed.contentBlocks;
    return Array.isArray(blocks) ? blocks : null;
  } catch (_unused) {
    return null;
  }
}
function isDataTextBlock(block) {
  return block.__typename === TextChirpContentBlockTypename || block.type === 'TEXT';
}
function isWrapperDisplayBlock(block) {
  return block.__typename === DisplayOnlyChirpContentBlockTypename || block.type === 'DISPLAY_ONLY';
}
function expandToolResultBlock(block) {
  if (typeof block.content !== 'string' || block.content.length === 0) {
    return null;
  }
  const nestedBlocks = parseWrapperBlocks(block.content);
  if (nestedBlocks === null) return null;
  const dataTextBlock = nestedBlocks.find(isDataTextBlock);
  const newContent = dataTextBlock && typeof dataTextBlock.text === 'string' ? dataTextBlock.text : block.content;
  const hoistedToolResult = Object.assign({}, block, {
    content: newContent
  });
  const displayBlocks = nestedBlocks.filter(isWrapperDisplayBlock).map(displayBlock => Object.assign({}, displayBlock, {
    __typename: DisplayOnlyChirpContentBlockTypename
  }));
  return [hoistedToolResult, ...displayBlocks];
}

// Keyed on the source message so a given input consistently yields the same
// expanded output reference. Without this, every render rebuilds the expanded
// object for wrapper messages, defeating the identity-keyed toolResultCitationCache.
const expandedMessageCache = new WeakMap();
function expandMessage(message) {
  const cached = expandedMessageCache.get(message);
  if (cached !== undefined) return cached;
  let changed = false;
  const newContent = [];
  for (const block of message.content) {
    if (block.__typename === ToolResultChirpContentBlockTypename) {
      const expanded = expandToolResultBlock(block);
      if (expanded !== null) {
        changed = true;
        newContent.push(...expanded);
        continue;
      }
    }
    newContent.push(block);
  }
  const result = changed ? Object.assign({}, message, {
    content: newContent
  }) : message;
  expandedMessageCache.set(message, result);
  return result;
}

// Tools that return a ContentBlockToolResponse can deliver their response
// sections nested inside the tool-result content as a `{"contentBlocks":[...]}`
// string rather than as sibling message blocks. Hoist the nested data text
// block (carrying the top-level citations) into the tool-result content and the
// nested DISPLAY_ONLY blocks into real siblings so the existing companion and
// citation pipeline can resolve them.
function expandContentBlockToolResponses(messages) {
  return messages.map(expandMessage);
}
function hasToolUseBlocks(message) {
  return message.role === 'ASSISTANT' && message.content.some(isToolUseBlock);
}
function isContextBlock(block) {
  return block.__typename === ContextChirpContentBlockTypename;
}
function isToolResultMessage(message) {
  return message.role === 'USER' && message.content.length > 0 && message.content.every(block => isToolResultContent(block) || isDisplayOnlyBlock(block) || isContextBlock(block)) && message.content.some(isToolResultContent);
}
function isPureToolResultMessage(message) {
  return message.role === 'USER' && message.content.length > 0 && message.content.every(block => isToolResultContent(block) || isContextBlock(block));
}
function groupMessages(messages) {
  const groups = [];
  let stashedToolResult;
  let i = 0;
  while (i < messages.length) {
    const msg = messages[i];
    if (msg.role === 'SYSTEM') {
      i++;
      continue;
    }
    if (hasToolUseBlocks(msg)) {
      let nextIdx = i + 1;
      while (nextIdx < messages.length && messages[nextIdx].role === 'SYSTEM') {
        nextIdx++;
      }
      const next = messages[nextIdx];
      const forwardResult = next && isToolResultMessage(next) ? next : undefined;
      const useIds = new Set(msg.content.filter(isToolUseBlock).map(block => getToolUseIdForResult(block)));
      const stashMatches = stashedToolResult !== undefined && stashedToolResult.content.some(block => isToolResultContent(block) && useIds.has(block.toolUseId));
      const resultMsg = stashMatches ? stashedToolResult : forwardResult;
      groups.push({
        type: 'tool-call',
        useMsg: msg,
        resultMsg
      });
      if (stashMatches) {
        stashedToolResult = undefined;
        i = i + 1;
      } else if (forwardResult) {
        stashedToolResult = undefined;
        i = nextIdx + 1;
      } else {
        i = i + 1;
      }
    } else if (isPureToolResultMessage(msg)) {
      stashedToolResult = msg;
      i++;
    } else {
      stashedToolResult = undefined;
      groups.push({
        type: 'regular',
        msg
      });
      i++;
    }
  }
  return groups;
}
function normalizeRole(role) {
  return role === 'USER' ? 'USER' : 'ASSISTANT';
}
function buildNormalizedMessage(msg, blocks, turnId, stableId) {
  const result = {
    id: msg.id,
    stableId: stableId !== null && stableId !== void 0 ? stableId : msg.id,
    role: normalizeRole(msg.role),
    timestamp: msg.timestamp,
    blocks
  };
  if (turnId !== undefined) {
    result.turnId = turnId;
  }
  if (msg.subagentSource) {
    result.subagentSource = {
      executionId: msg.subagentSource.executionId,
      subagentName: msg.subagentSource.subagentName,
      toolCallId: msg.subagentSource.toolCallId,
      depth: msg.subagentSource.depth
    };
  }
  if (msg.parentMessageId !== undefined) {
    result.parentMessageId = msg.parentMessageId;
  }
  if (msg.groupId !== undefined) {
    result.groupId = msg.groupId;
  }
  if (msg.phase !== undefined) {
    result.phase = msg.phase;
  }
  if (msg.stopReason !== undefined) {
    result.stopReason = msg.stopReason;
  }
  if (msg.timing != null && typeof msg.timing.initiatedAt === 'number') {
    result.timing = {
      initiatedAt: msg.timing.initiatedAt
    };
    if (typeof msg.timing.streamingEndTime === 'number') {
      result.timing.streamingEndTime = msg.timing.streamingEndTime;
    }
  }
  return result;
}
function deduplicateSources(sections) {
  const seenUrls = new Set();
  const buckets = new Map();
  for (const section of sections) {
    const title = typeof section.title === 'string' ? section.title : '';
    // S5: single get instead of has+get
    let bucket = buckets.get(title);
    if (!bucket) {
      bucket = {
        base: section,
        sources: []
      };
      buckets.set(title, bucket);
    }
    const sources = section.sources;
    // S4: forward sections without a sources array as-is rather than dropping them
    if (!Array.isArray(sources)) {
      if (bucket.sources.length === 0) bucket.base = section;
      continue;
    }
    for (const source of sources) {
      const data = source.data;
      const url = typeof (data === null || data === void 0 ? void 0 : data.url) === 'string' ? data.url : undefined;
      if (url && seenUrls.has(url)) continue;
      if (url) seenUrls.add(url);
      bucket.sources.push(source);
    }
  }
  return Array.from(buckets.values()).map(({
    base,
    sources
  }) => sources.length > 0 ? Object.assign({}, base, {
    sources
  }) : base);
}
function mergeSectionGroup(entries) {
  // S3: resolve sectionField from the first entry that has the key, not the last
  let sectionField = null;
  let baseEntry = entries[entries.length - 1];
  for (const entry of entries) {
    const v = entry.value;
    if ('sourcesSections' in v) {
      sectionField = 'sourcesSections';
      baseEntry = entry;
      break;
    }
    if ('sections' in v) {
      sectionField = 'sections';
      baseEntry = entry;
      break;
    }
  }
  if (!sectionField) return baseEntry;
  const baseValue = baseEntry.value;
  const allSections = entries.flatMap(entry => {
    const sections = entry.value[sectionField];
    return Array.isArray(sections) ? sections : [];
  });
  return Object.assign({}, baseEntry, {
    value: Object.assign({}, baseValue, {
      [sectionField]: deduplicateSources(allSections)
    })
  });
}
function mergeDuplicateSectionEntries(entries) {
  // S2: single-pass partition instead of two filter calls
  const companionEntries = [];
  const standaloneEntries = [];
  for (const entry of entries) {
    (entry.citationId !== undefined ? companionEntries : standaloneEntries).push(entry);
  }
  const grouped = new Map();
  for (const entry of standaloneEntries) {
    const group = grouped.get(entry.__typename);
    if (group) {
      group.push(entry);
    } else {
      grouped.set(entry.__typename, [entry]);
    }
  }
  const mergedStandalone = [];
  for (const group of grouped.values()) {
    // S1: always call mergeSectionGroup so within-entry URL dedup runs even for
    // single-round conversations (group.length === 1)
    mergedStandalone.push(mergeSectionGroup(group));
  }
  return [...companionEntries, ...mergedStandalone];
}
function mergeToolResultDisplayOnly(blocks) {
  const blocksWithEntries = blocks.filter(block => block.entries && block.entries.length > 0);
  const blocksWithTextOnly = blocks.filter(block => (!block.entries || block.entries.length === 0) && block.text);
  const result = [];
  if (blocksWithEntries.length > 0) {
    const allEntries = blocksWithEntries.flatMap(block => {
      var _block$entries;
      return (_block$entries = block.entries) !== null && _block$entries !== void 0 ? _block$entries : [];
    });
    result.push({
      type: 'DISPLAY_ONLY',
      entries: mergeDuplicateSectionEntries(allEntries),
      source: 'tool-result'
    });
  }
  for (const block of blocksWithTextOnly) {
    result.push(Object.assign({}, block, {
      source: 'tool-result'
    }));
  }
  return result;
}
function hasTextBlock(message) {
  return message.blocks.some(block => block.type === 'TEXT');
}
let displayOnlySyntheticId = 0;
function attachPendingDisplayOnly(groups) {
  const result = [];
  const pending = [];
  for (const group of groups) {
    for (const message of group.messages) {
      // A USER text message starts a new turn — drop pending so blocks
      // don't bleed across turns.
      if (message.role === 'USER' && pending.length > 0 && hasTextBlock(message)) {
        pending.length = 0;
      }
      // Attach to the next TEXT ASSISTANT. Skip ASSISTANTs with only
      // display-only blocks (e.g. timeline entries): BreezeMessageWrapper
      // renders just the last one, so cards attached here get dropped.
      if (message.role === 'ASSISTANT' && pending.length > 0 && hasTextBlock(message)) {
        message.blocks.push(...mergeToolResultDisplayOnly(pending));
        pending.length = 0;
      }
      result.push(message);
    }
    pending.push(...group.displayOnly);
  }

  // End-of-turn fallback: walk back to the last TEXT ASSISTANT in the
  // current turn. Needed when the final tool call emits display-only
  // blocks (e.g. suggestAddToPage) with no following ASSISTANT response.
  if (pending.length > 0) {
    let target = -1;
    for (let i = result.length - 1; i >= 0; i--) {
      // Stop at the prior turn boundary.
      if (result[i].role === 'USER' && hasTextBlock(result[i])) {
        break;
      }
      if (result[i].role === 'ASSISTANT' && hasTextBlock(result[i])) {
        target = i;
        break;
      }
    }
    if (target >= 0) {
      result[target].blocks.push(...mergeToolResultDisplayOnly(pending));
    } else {
      var _lastMsg$id, _lastMsg$stableId, _lastMsg$timestamp;
      const lastMsg = result[result.length - 1];
      const syntheticBaseId = (_lastMsg$id = lastMsg === null || lastMsg === void 0 ? void 0 : lastMsg.id) !== null && _lastMsg$id !== void 0 ? _lastMsg$id : `orphan-${displayOnlySyntheticId++}`;
      const syntheticMsg = {
        id: `${syntheticBaseId}-display-only`,
        stableId: `${(_lastMsg$stableId = lastMsg === null || lastMsg === void 0 ? void 0 : lastMsg.stableId) !== null && _lastMsg$stableId !== void 0 ? _lastMsg$stableId : syntheticBaseId}-display-only`,
        role: 'ASSISTANT',
        timestamp: (_lastMsg$timestamp = lastMsg === null || lastMsg === void 0 ? void 0 : lastMsg.timestamp) !== null && _lastMsg$timestamp !== void 0 ? _lastMsg$timestamp : 0,
        blocks: mergeToolResultDisplayOnly(pending)
      };
      if ((lastMsg === null || lastMsg === void 0 ? void 0 : lastMsg.turnId) !== undefined) {
        syntheticMsg.turnId = lastMsg.turnId;
      }
      result.push(syntheticMsg);
    }
  }
  return result;
}
function normalizeToolCallGroup(group, subagentMap, context = {}) {
  var _context$turnIdMap, _context$stableIdMap$, _context$stableIdMap, _resultMsg$content;
  const {
    useMsg,
    resultMsg
  } = group;
  const messages = [];
  const msgTurnId = (_context$turnIdMap = context.turnIdMap) === null || _context$turnIdMap === void 0 ? void 0 : _context$turnIdMap.get(useMsg.id);
  const msgStableId = (_context$stableIdMap$ = (_context$stableIdMap = context.stableIdMap) === null || _context$stableIdMap === void 0 ? void 0 : _context$stableIdMap.get(useMsg.id)) !== null && _context$stableIdMap$ !== void 0 ? _context$stableIdMap$ : useMsg.id;
  const nonToolBlocks = useMsg.content.filter(block => !isToolUseBlock(block)).map(block => normalizeBlock(block)).filter(block => block !== null).filter(block => block.type !== 'TEXT' || block.text.trim() !== '');
  if (nonToolBlocks.length > 0) {
    const textMsg = buildNormalizedMessage(useMsg, nonToolBlocks, msgTurnId);
    textMsg.id = `${useMsg.id}-text`;
    textMsg.stableId = `${msgStableId}-text`;
    messages.push(textMsg);
  }
  const companionMap = buildCompanionMap(resultMsg);
  const companionBlocks = new Set(Array.from(companionMap.values()).flat());
  const allPairs = buildToolPairs(useMsg, resultMsg, subagentMap);
  const {
    promotedEntries,
    nonPromotedPairs
  } = promoteCompanionEntries(allPairs, companionMap);

  // Extract ARTIFACT_WRITE entries from DISPLAY_ONLY blocks first so those
  // blocks are excluded from nonCompanionDisplayBlocks (they would otherwise
  // produce empty DISPLAY_ONLY entries since parseDisplayOnlyEntries can't
  // handle the entryType-keyed records).
  const {
    entries: artifactWriteEntries,
    consumedBlocks: artifactWriteBlocks
  } = extractArtifactWriteEntries((_resultMsg$content = resultMsg === null || resultMsg === void 0 ? void 0 : resultMsg.content) !== null && _resultMsg$content !== void 0 ? _resultMsg$content : []);
  const displayBlockArtifacts = buildArtifactBlocksFromWriteEntries(artifactWriteEntries);
  const displayBlockVersionIds = new Set(displayBlockArtifacts.map(block => block.versionId).filter(id => id !== undefined));
  const nonCompanionDisplayBlocks = resultMsg ? resultMsg.content.filter(block => isDisplayOnlyBlock(block) && !companionBlocks.has(block) && !artifactWriteBlocks.has(block)).map(block => normalizeBlock(block)).filter(block => block !== null) : [];
  const inlineBlocks = nonCompanionDisplayBlocks.filter(block => block.type !== 'DISPLAY_ONLY');
  const detectedArtifacts = extractArtifactToolCalls(allPairs, true);
  // Prefer display block artifacts over legacy filesystem artifacts for the
  // same versionId. The display block path covers tool types (e.g. Bash) that
  // the legacy artifactWrites field never populated.
  const legacyArtifactBlocks = buildArtifactBlocksFromDetected(detectedArtifacts).filter(block => block.versionId === undefined || !displayBlockVersionIds.has(block.versionId));
  const filesystemArtifactBlocks = [...displayBlockArtifacts, ...legacyArtifactBlocks];

  // A Write/Edit/MultiEdit to /artifacts/ is represented by its artifact card,
  // so drop the raw tool-call pair to avoid showing both the card and the row.
  const artifactToolUseIds = new Set(detectedArtifacts.map(detected => detected.toolUseId));
  const visiblePairs = nonPromotedPairs.filter(pair => !artifactToolUseIds.has(pair.input.id));
  const toolMsgBlocks = [...inlineBlocks];
  if (visiblePairs.length > 0) {
    toolMsgBlocks.unshift({
      type: 'TOOL',
      turnId: useMsg.id,
      pairs: visiblePairs
    });
  }
  toolMsgBlocks.push(...filesystemArtifactBlocks);
  if (toolMsgBlocks.length > 0) {
    const toolMsg = buildNormalizedMessage(useMsg, toolMsgBlocks, msgTurnId);
    toolMsg.id = `${useMsg.id}-tool`;
    toolMsg.stableId = `${msgStableId}-tool`;
    messages.push(toolMsg);
  }
  const displayOnly = [...nonCompanionDisplayBlocks.filter(block => block.type === 'DISPLAY_ONLY').map(block => Object.assign({}, block, {
    source: 'tool-result'
  })),
  // Companion entries (those with citationId) float to the next assistant
  // message instead of rendering standalone in the tool message. They are
  // picked up by buildCitationIdToEntryMap for inline resolution into
  // TextBlock.entries, and filtered from standalone rendering by citationId.
  ...(promotedEntries.length > 0 ? [{
    type: 'DISPLAY_ONLY',
    entries: promotedEntries,
    source: 'tool-result'
  }] : [])];
  return {
    messages,
    displayOnly
  };
}
function normalizeRegularGroup(msg, context = {}) {
  var _context$turnIdMap2, _context$stableIdMap2;
  let parsedContext = null;
  let contextSourceBlock;
  let strippedText;
  if (msg.role === 'USER') {
    contextSourceBlock = msg.content.find(contentBlock => contentBlock.__typename === TextChirpContentBlockTypename);
    if (contextSourceBlock) {
      parsedContext = extractHiddenContext(contextSourceBlock.text);
      if (parsedContext) {
        strippedText = stripHiddenContext(contextSourceBlock.text);
      }
    }
  }
  const blocks = msg.content.map(block => normalizeBlock(block, block === contextSourceBlock ? strippedText : undefined)).filter(normalizedBlock => normalizedBlock !== null);
  const normalized = buildNormalizedMessage(msg, blocks, (_context$turnIdMap2 = context.turnIdMap) === null || _context$turnIdMap2 === void 0 ? void 0 : _context$turnIdMap2.get(msg.id), (_context$stableIdMap2 = context.stableIdMap) === null || _context$stableIdMap2 === void 0 ? void 0 : _context$stableIdMap2.get(msg.id));
  if (parsedContext) {
    normalized.messageContext = parsedContext;
  }
  return {
    messages: [normalized],
    displayOnly: []
  };
}
function normalizeMessageList(messages, subagentMap, context = {}) {
  const groups = groupMessages(messages);
  const normalizedGroups = groups.map(group => group.type === 'tool-call' ? normalizeToolCallGroup(group, subagentMap, context) : normalizeRegularGroup(group.msg, context));
  const result = dedupFilesystemArtifacts(attachPendingDisplayOnly(normalizedGroups));
  const usedToolCallIds = new Set(groups.filter(group => group.type === 'tool-call').flatMap(group => {
    const toolUseIds = group.useMsg.content.filter(isToolUseBlock).map(block => getToolUseIdForResult(block));
    const resultIds = group.resultMsg ? group.resultMsg.content.filter(isToolResultContent).map(block => block.toolUseId) : [];
    return [...toolUseIds, ...resultIds];
  }));
  for (const [toolCallId] of subagentMap) {
    if (!usedToolCallIds.has(toolCallId) && result.length > 0) {
      console.warn(`Dropping orphan subagent messages for toolCallId="${toolCallId}" — no matching parent tool call found`);
    }
  }
  return result;
}
function dedupFilesystemArtifacts(messages) {
  // Dedup is scoped to a single turn: a file written then edited within the
  // same turn collapses to its latest card, but each turn that touches the
  // artifact keeps its own card. A USER text message starts a new turn.
  const turnSegments = [];
  let segment = 0;
  for (const message of messages) {
    if (message.role === 'USER' && hasTextBlock(message)) {
      segment += 1;
    }
    turnSegments.push(segment);
  }
  const latestIndexByKey = new Map();
  messages.forEach((message, index) => {
    for (const block of message.blocks) {
      if (block.type !== 'ARTIFACT') continue;
      const artifact = block;
      if (artifact.source !== 'filesystem') continue;
      if (artifact.path) {
        latestIndexByKey.set(`${turnSegments[index]} ${artifact.path}`, index);
      }
    }
  });
  if (latestIndexByKey.size === 0) return messages;
  return messages.map((message, index) => {
    let changed = false;
    const updatedBlocks = message.blocks.filter(block => {
      if (block.type !== 'ARTIFACT') return true;
      const artifact = block;
      if (artifact.source !== 'filesystem' || !artifact.path) return true;
      const key = `${turnSegments[index]} ${artifact.path}`;
      const isLatest = latestIndexByKey.get(key) === index;
      if (!isLatest) changed = true;
      return isLatest;
    });
    return changed ? Object.assign({}, message, {
      blocks: updatedBlocks
    }) : message;
  });
}
const toolResultCitationCache = new WeakMap();
function collectToolResultCitations(messages, resolveCitationUrl) {
  const citations = [];
  for (const msg of messages) {
    const cached = toolResultCitationCache.get(msg);
    if (cached !== undefined) {
      citations.push(...cached);
      continue;
    }
    const msgCitations = [];
    for (const block of msg.content) {
      if (block.__typename === ToolResultChirpContentBlockTypename) {
        msgCitations.push(...parseCitationsFromToolResult(block.content, resolveCitationUrl));
      }
    }
    toolResultCitationCache.set(msg, msgCitations);
    citations.push(...msgCitations);
  }
  return citations;
}
function collectDisplayOnlyCitations(messages) {
  const citations = [];
  for (const msg of messages) {
    for (const block of msg.blocks) {
      if (block.type !== 'DISPLAY_ONLY') continue;
      for (const entry of (_entries = block.entries) !== null && _entries !== void 0 ? _entries : []) {
        var _entries;
        if (!entry.citationId) continue;
        const citation = convertAppCitationEntry(entry);
        if (citation) citations.push(citation);
      }
    }
  }
  return citations;
}

// Extract source.data.url from a SourceItem-shaped object in a SourcesListResponseSection.
function getSourceUrl(source) {
  const data = source.data;
  return typeof (data === null || data === void 0 ? void 0 : data.url) === 'string' ? data.url : undefined;
}

// Build a map of cited URL → inline citation index from resolved TEXT block citations.
// Used to filter and reorder the sources panel to match what the LLM actually cited.
function collectCitedUrls(messages) {
  const urlToIndex = new Map();
  for (const msg of messages) {
    for (const block of msg.blocks) {
      if (block.type !== 'TEXT') continue;
      for (const citation of (_citations = block.citations) !== null && _citations !== void 0 ? _citations : []) {
        var _citations;
        const url = citation.url;
        if (!url || urlToIndex.has(url)) continue;
        const idx = 'index' in citation ? citation.index : undefined;
        urlToIndex.set(url, idx !== null && idx !== void 0 ? idx : urlToIndex.size);
      }
    }
  }
  return urlToIndex;
}

// Filter SourcesListResponseSection entries in DISPLAY_ONLY blocks to only include
// sources the LLM cited, and reorder them to match inline citation appearance order.
// Skipped when there is no citation data from any source.
function filterDisplayOnlySources(messages, hasCitationData) {
  const citedUrls = collectCitedUrls(messages);
  if (!hasCitationData && citedUrls.size === 0) return messages;
  let anyChanged = false;
  const result = messages.map(msg => {
    let msgChanged = false;
    const newBlocks = msg.blocks.map(block => {
      var _displayOnly$entries;
      if (block.type !== 'DISPLAY_ONLY') return block;
      const displayOnly = block;
      if (!((_displayOnly$entries = displayOnly.entries) !== null && _displayOnly$entries !== void 0 && _displayOnly$entries.length)) return block;
      let entriesChanged = false;
      const newEntries = [];
      for (const entry of displayOnly.entries) {
        const value = entry.value;
        // SourcesListResponseSection uses either 'sourcesSections' or 'sections'.
        // Non-sources entries (images, videos, cards) pass through unchanged.
        const field = 'sourcesSections' in value ? 'sourcesSections' : 'sections' in value ? 'sections' : null;
        if (!field || !Array.isArray(value[field])) {
          newEntries.push(entry);
          continue;
        }

        // Keep only sources whose URL was cited, then sort by citation appearance order.
        // Sources without a URL are kept (fail-open). Empty sections are dropped.
        const sections = value[field];
        let sectionChanged = false;
        const filtered = sections.map(section => {
          if (!Array.isArray(section.sources)) return section;
          const original = section.sources;
          const sources = original.filter(source => {
            const url = getSourceUrl(source);
            return !url || citedUrls.has(url);
          }).sort((sourceA, sourceB) => {
            var _citedUrls$get, _getSourceUrl, _citedUrls$get2, _getSourceUrl2;
            const indexA = (_citedUrls$get = citedUrls.get((_getSourceUrl = getSourceUrl(sourceA)) !== null && _getSourceUrl !== void 0 ? _getSourceUrl : '')) !== null && _citedUrls$get !== void 0 ? _citedUrls$get : Infinity;
            const indexB = (_citedUrls$get2 = citedUrls.get((_getSourceUrl2 = getSourceUrl(sourceB)) !== null && _getSourceUrl2 !== void 0 ? _getSourceUrl2 : '')) !== null && _citedUrls$get2 !== void 0 ? _citedUrls$get2 : Infinity;
            return indexA - indexB;
          });
          const thisChanged = sources.length !== original.length || sources.some((source, idx) => source !== original[idx]);
          if (thisChanged) sectionChanged = true;
          return thisChanged ? Object.assign({}, section, {
            sources
          }) : section;
        }).filter(section => !Array.isArray(section.sources) || section.sources.length > 0);
        if (filtered.length !== sections.length) {
          sectionChanged = true;
        }
        if (!sectionChanged) {
          newEntries.push(entry);
          continue;
        }
        entriesChanged = true;
        if (filtered.length > 0) {
          newEntries.push(Object.assign({}, entry, {
            value: Object.assign({}, value, {
              [field]: filtered
            })
          }));
        }
      }
      if (!entriesChanged) return block;
      msgChanged = true;
      anyChanged = true;
      return Object.assign({}, displayOnly, {
        entries: newEntries
      });
    });
    return msgChanged ? Object.assign({}, msg, {
      blocks: newBlocks
    }) : msg;
  });
  return anyChanged ? result : messages;
}

// Run messages through the normalize pipeline and collect the citation set that
// resolveCitations consumes, captured *before* resolveCitations strips the
// citationId-bearing DISPLAY_ONLY entries. Exported for collectCitationTitles
// (sibling util) so it can build a cite-id -> title map without re-implementing
// the pipeline; not part of the public consumer API.
function deduplicateMessagesById(messages) {
  const seen = new Set();
  return messages.filter(msg => {
    if (!msg.id) return true;
    if (seen.has(msg.id)) return false;
    seen.add(msg.id);
    return true;
  });
}
export function gatherNormalizedCitations(messages, context = {}) {
  const deduped = deduplicateMessagesById(messages);
  const expandedMessages = expandContentBlockToolResponses(deduped);
  const subagentRaw = [];
  const topLevel = [];
  for (const msg of expandedMessages) {
    if (msg.subagentSource) {
      subagentRaw.push(msg);
    } else {
      topLevel.push(msg);
    }
  }
  const subagentMap = new Map();
  const groupedByToolCallId = new Map();
  for (const msg of subagentRaw) {
    const toolCallId = msg.subagentSource.toolCallId;
    const existing = groupedByToolCallId.get(toolCallId);
    if (existing) {
      existing.push(msg);
    } else {
      groupedByToolCallId.set(toolCallId, [msg]);
    }
  }
  for (const [toolCallId, msgs] of groupedByToolCallId) {
    const normalized = normalizeMessageList(msgs, new Map(), context);
    if (normalized.length > 0) {
      subagentMap.set(toolCallId, normalized);
      continue;
    }
    const synthetic = [];
    for (const msg of msgs) {
      if (msg.role !== 'USER') continue;
      const pairs = [];
      for (const block of msg.content) {
        if (!isToolResultContent(block)) {
          reportWarning('Synthetic subagent path: unexpected block type in USER message', {
            scope: 'normalizeMessages.syntheticSubagentPairs',
            extra: {
              typename: block.__typename,
              messageId: msg.id,
              toolCallId
            }
          });
          continue;
        }
        const result = block;
        pairs.push({
          input: {
            id: result.toolUseId,
            name: result.toolName,
            input: {},
            serverInitiated: true
          },
          output: {
            type: 'TEXT',
            toolUseId: result.toolUseId,
            toolName: result.toolName,
            content: result.content,
            isError: result.error
          }
        });
      }
      if (pairs.length > 0) {
        synthetic.push(Object.assign({
          id: msg.id,
          role: 'ASSISTANT',
          timestamp: msg.timestamp,
          blocks: [{
            type: 'TOOL',
            turnId: msg.id,
            pairs
          }]
        }, msg.subagentSource ? {
          subagentSource: {
            executionId: msg.subagentSource.executionId,
            subagentName: msg.subagentSource.subagentName,
            toolCallId: msg.subagentSource.toolCallId,
            depth: msg.subagentSource.depth
          }
        } : {}));
      }
    }
    subagentMap.set(toolCallId, synthetic);
  }
  const rawCitations = collectToolResultCitations(topLevel, context.resolveCitationUrl);
  const normalized = normalizeMessageList(topLevel, subagentMap, context);
  const filtered = filterSupersededDisplayOnlyEntries(normalized);
  const displayOnlyCitations = collectDisplayOnlyCitations(filtered);
  const seen = new Set();
  const allCitations = [...rawCitations, ...displayOnlyCitations].filter(citation => {
    if (seen.has(citation.id)) return false;
    seen.add(citation.id);
    return true;
  });
  return {
    filtered,
    allCitations
  };
}

// Filesystem artifacts (Write/Edit/MultiEdit to /artifacts/) carry no version:
// the backend emits no per-write content block and the write-through tool result
// is silent. But versioning is deterministic — the first write to a path is v1
// and each subsequent write publishes the next version — so derive the per-turn
// version by counting writes to each path in transcript order. listArtifacts
// only knows the current version, so it can't tell a create card apart from a
// later edit card.
//
// Edit/MultiEdit tool calls also carry no full file content (only Write calls
// do — see extractArtifactToolCallsFromBlocks/getFileContent), so a version
// produced by an edit has no payload of its own. Carry forward the last known
// payload for that path so the chip/canvas render the prior content instead of
// a blank one; it may be momentarily stale until the artifact is persisted and
// a live GetArtifact query supplies the true latest content.
function assignFilesystemArtifactVersions(messages) {
  const versionByPath = new Map();
  const payloadByPath = new Map();
  let changed = false;
  const updated = messages.map(message => {
    let blocksChanged = false;
    const updatedBlocks = message.blocks.map(block => {
      var _artifact$version, _versionByPath$get;
      if (block.type !== 'ARTIFACT') return block;
      const artifact = block;
      if (artifact.source !== 'filesystem' || !artifact.path) return block;
      const version = (_artifact$version = artifact.version) !== null && _artifact$version !== void 0 ? _artifact$version : ((_versionByPath$get = versionByPath.get(artifact.path)) !== null && _versionByPath$get !== void 0 ? _versionByPath$get : 0) + 1;
      versionByPath.set(artifact.path, version);
      if (artifact.payload) {
        payloadByPath.set(artifact.path, artifact.payload);
        if (artifact.version === version) return block;
        blocksChanged = true;
        return Object.assign({}, artifact, {
          version
        });
      }
      const carriedPayload = payloadByPath.get(artifact.path);
      if (artifact.version === version && !carriedPayload) return block;
      blocksChanged = true;
      return Object.assign({}, artifact, {
        version
      }, carriedPayload && {
        payload: carriedPayload
      });
    });
    if (!blocksChanged) return message;
    changed = true;
    return Object.assign({}, message, {
      blocks: updatedBlocks
    });
  });
  return changed ? updated : messages;
}
export function normalizeMessages(messages, context = {}) {
  const {
    filtered,
    allCitations
  } = gatherNormalizedCitations(messages, context);
  const withCitations = resolveCitations(filtered, allCitations, context.conversationId, context.isTurnInProgress);
  const result = assignFilesystemArtifactVersions(filterDisplayOnlySources(withCitations, allCitations.length > 0));
  const {
    feedbackByTurnId,
    feedbackByCitationKey
  } = context;
  const hasTurnFeedback = feedbackByTurnId && feedbackByTurnId.size > 0;
  const hasCitationFeedback = feedbackByCitationKey && feedbackByCitationKey.size > 0;
  if (!hasTurnFeedback && !hasCitationFeedback) return result;
  return result.map(message => {
    var _message$feedback;
    if (message.turnId === undefined) return message;
    const turnFeedbackType = hasTurnFeedback ? feedbackByTurnId.get(message.turnId) : undefined;
    let updatedMessage = message;
    if (turnFeedbackType && ((_message$feedback = message.feedback) === null || _message$feedback === void 0 ? void 0 : _message$feedback.type) !== turnFeedbackType) {
      updatedMessage = Object.assign({}, updatedMessage, {
        feedback: {
          type: turnFeedbackType
        }
      });
    }
    if (hasCitationFeedback) {
      let blocksChanged = false;
      const updatedBlocks = updatedMessage.blocks.map(block => {
        var _textBlock$citations;
        if (block.type !== 'TEXT') return block;
        const textBlock = block;
        if (!((_textBlock$citations = textBlock.citations) !== null && _textBlock$citations !== void 0 && _textBlock$citations.length)) return block;
        let changed = false;
        const updatedCitations = textBlock.citations.map(citation => {
          var _citationWithFeedback;
          const citationKey = `${message.turnId}:${citation.id}`;
          const citationFeedbackType = feedbackByCitationKey.get(citationKey);
          if (!citationFeedbackType) return citation;
          const polarity = citationFeedbackType === 'THUMBS_UP' ? 'POSITIVE' : 'NEGATIVE';
          // CrmChipCitation omits feedback (chips don't render thumbs); cast to access
          // the field on WebCitation. Chips never reach this path since they have no index.
          const citationWithFeedback = citation;
          if (((_citationWithFeedback = citationWithFeedback.feedback) === null || _citationWithFeedback === void 0 ? void 0 : _citationWithFeedback.polarity) === polarity) return citation;
          changed = true;
          return Object.assign({}, citation, {
            feedback: {
              polarity
            }
          });
        });
        if (!changed) return block;
        blocksChanged = true;
        return Object.assign({}, textBlock, {
          citations: updatedCitations
        });
      });
      if (blocksChanged) {
        updatedMessage = Object.assign({}, updatedMessage, {
          blocks: updatedBlocks
        });
      }
    }
    return updatedMessage;
  });
}
function filterSupersededDisplayOnlyEntries(messages) {
  // Pass 1: for each section ID, record the index of the last message that
  // contains it. Entries in earlier messages are superseded by entries in
  // later ones — this happens when appendTranscript injects a COMPLETED
  // replacement for a PENDING section.
  const latestIndexBySectionId = new Map();
  messages.forEach((message, index) => {
    for (const block of message.blocks) {
      if (block.type !== 'DISPLAY_ONLY') continue;
      for (const entry of (_entries2 = block.entries) !== null && _entries2 !== void 0 ? _entries2 : []) {
        var _entries2;
        const sectionId = entry.id || null;
        if (sectionId !== null) {
          latestIndexBySectionId.set(sectionId, index);
        }
      }
    }
  });
  if (latestIndexBySectionId.size === 0) return messages;

  // Pass 2: strip superseded entries from each message. Preserves reference
  // equality for messages and blocks that need no change.
  return messages.map((message, index) => {
    const updatedBlocks = message.blocks.map(block => {
      var _displayOnly$entries2;
      if (block.type !== 'DISPLAY_ONLY') return block;
      const displayOnly = block;
      if (!((_displayOnly$entries2 = displayOnly.entries) !== null && _displayOnly$entries2 !== void 0 && _displayOnly$entries2.length)) return block;
      const keptEntries = displayOnly.entries.filter(entry => {
        const sectionId = entry.id || null;
        return sectionId === null || latestIndexBySectionId.get(sectionId) === index;
      });
      return keptEntries.length === displayOnly.entries.length ? block : Object.assign({}, displayOnly, {
        entries: keptEntries
      });
    });
    const hasChanges = updatedBlocks.some((block, blockIndex) => block !== message.blocks[blockIndex]);
    return hasChanges ? Object.assign({}, message, {
      blocks: updatedBlocks
    }) : message;
  });
}